import React, { useState, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import {
  type GridSelectionModel,
  type GridFilterModel,
  type GridSortModel,
  // GridLinkOperator,
} from "@material-ui/x-grid";

import TimeFilter, { getPeriodFromPreset } from "src/components/TimeFilter";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { useDateRange } from "src/utils/timeFilter";
import TableHeader, { TableHeaderButton } from "src/components/TableHeader";
import XGrid from "src/components/ConfiguredXGrid";
import CSVFormattedXGrid from "src/components/CSVFormattedXGrid";
import {
  createTableToolbar,
  DefaultFeatures,
} from "src/components/TableToolbar";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { calendarYmdInTimeZone } from "src/utils/timezone";
import { useFullscreen } from "src/utils/fullscreen";
import { filteredExpensesSelector } from "src/store/transaction/selector";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { deleteMappedTransactions } from "src/apiService/modules/mapped-transactions";
import type { MappedTransaction } from "src/interfaces/transaction.interface";
import ConfirmDialog from "src/components/ConfirmDialog";
import {
  isSplitTransaction,
  notifySkippedSplitTransactions,
} from "./splitTransactions";

import BulkEditDialog from "./Expenses/BulkEditDialog";
import BulkEditPanel from "./Expenses/BulkEditPanel";
import { TransactionSalesOtherColumns as Columns } from "./Sales/columns";

const OtherTableToolbar = createTableToolbar({
  features: {
    ...DefaultFeatures,
    Fullscreen: true,
  },
  options: { fileName: "My Reseller Genie - Other" },
});

function Other() {
  const recordEvent = useRecordEvent();

  const dispatch = useDispatch();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const sectionRef = useRef<HTMLDivElement>();
  const { timeOption: timeFilter, setDateRange } = useDateRange("transaction");
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "date", sort: "desc" },
  ]);
  const requestFullscreen = useFullscreen();
  const filters = useMemo(() => {
    const period =
      timeFilter.type === "custom"
        ? timeFilter.period
        : getPeriodFromPreset(timeFilter, timeZone);
    const models: GridFilterModel[] = [
      {
        items: [
          {
            columnField: "date",
            operatorValue: "onOrAfter",
            value: calendarYmdInTimeZone(period.start, timeZone),
          },
          {
            columnField: "date",
            operatorValue: "onOrBefore",
            value: calendarYmdInTimeZone(period.end, timeZone),
          },
          {
            columnField: "type",
            operatorValue: "notEquals",
            value: "sale",
          },
        ],
      },
    ];

    return models;
  }, [timeFilter, timeZone]);
  const _transactions = useSelector((state) =>
    filteredExpensesSelector(state, filters)
  );
  const transactions = useMemo(() => {
    return _transactions.filter((s) => {
      const type = s.accountDoc?.type || [];
      return (
        type[0] !== "expenses" &&
        type[1] !== "inventory" &&
        type[0] !== "income"
      );
    });
  }, [_transactions]);
  const { editableTransactionIds, readOnlyCount } = useMemo(() => {
    const ids: string[] = [];
    let readOnly = 0;
    for (const id of selectionModel) {
      const row = transactions.find((r) => r.id === id);
      if (!row) continue;
      if (row.read_only) {
        readOnly += 1;
      } else {
        ids.push(String(id));
      }
    }
    return { editableTransactionIds: ids, readOnlyCount: readOnly };
  }, [selectionModel, transactions]);

  const [dialog, setDialog] = useState<
    | {
        type: "delete";
        items: Pick<MappedTransaction, "collection" | "collection_id">[];
        hasReadOnly?: boolean;
        skippedSplit?: number;
        length?: number;
      }
    | { type: "bulk-edit" }
    | { type: "bulk-edit-panel" }
  >();

  return (
    <div>
      <TimeFilter value={timeFilter} onChange={(v) => setDateRange(v)} />
      <Box
        {...({ ref: sectionRef } as any)}
        component={Paper}
        display="flex"
        flexDirection="column"
        minHeight={730}
        height="auto"
        p={2.5}
      >
        <TableHeader title="Other Transactions">
          <TableHeaderButton
            color="primary"
            onClick={() => {
              setDialog({ type: "bulk-edit-panel" });
            }}
            disabled={
              selectionModel.length === 0 || editableTransactionIds.length === 0
            }
            tooltip={
              selectionModel.length === 0
                ? "Select transactions to bulk edit."
                : editableTransactionIds.length === 0
                ? "Beginning Balance journals can't be bulk edited."
                : undefined
            }
          >
            Bulk Edit Transactions
          </TableHeaderButton>
          <TableHeaderButton
            colorType="error"
            disabled={selectionModel.length === 0}
            onClick={() => {
              let hasReadOnly = false;
              let skippedSplit = 0;
              const items: Pick<
                MappedTransaction,
                "collection_id" | "collection"
              >[] = [];

              for (const id of selectionModel) {
                const item = transactions.find((r) => r.id === id);
                if (item) {
                  if (isSplitTransaction(item)) {
                    // Split transactions can't be bulk deleted; they must be
                    // edited/deleted individually so the whole group is handled.
                    skippedSplit += 1;
                  } else if (item.read_only) {
                    hasReadOnly ||= true;
                  } else {
                    items.push({
                      collection: item.collection || "transaction",
                      collection_id: item.collection_id || item.id,
                    });
                  }
                }
              }

              setDialog({
                type: "delete",
                items,
                hasReadOnly,
                skippedSplit,
                length: selectionModel.length - skippedSplit,
              });
            }}
          >
            Delete Selected
          </TableHeaderButton>
        </TableHeader>
        <XGrid
          checkboxSelection
          tableName="all"
          Component={CSVFormattedXGrid}
          rows={transactions}
          columns={Columns}
          headerHeight={35}
          rowHeight={35}
          components={{
            Toolbar: OtherTableToolbar,
          }}
          componentsProps={{
            toolbar: {
              onFullscreenClick: () => {
                if (document.fullscreenElement) {
                  document.exitFullscreen();
                  return;
                }
                if (!sectionRef.current) return;
                requestFullscreen(sectionRef.current);
                recordEvent({
                  type: "feature-adoption",
                  feature: "fullscreen",
                  element: "other-table",
                });
              },
              onQuickFilterBlur: (e) => {
                const text = e.target.value;
                if (!text) return;
                recordEvent({
                  type: "feature-adoption",
                  feature: "quick-filter",
                  element: "other",
                  text,
                });
              },
            },
          }}
          onFilterModelChange={(model) => {
            recordEvent({
              type: "feature-adoption",
              feature: "table-filter",
              element: "other",
              filter: JSON.stringify(model),
            });
          }}
          selectionModel={selectionModel}
          onSelectionModelChange={(selectionModel: GridSelectionModel) => {
            setSelectionModel(selectionModel);
          }}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          onCellDoubleClick={(ev) => {
            if (ev.row?.journal_entry_id) {
              dispatch(
                setActiveDialog("add_journal", {
                  id: ev.row.journal_entry_id,
                })
              );
              return;
            }
            if (ev.row?.split_group_id) {
              dispatch(
                setActiveDialog("edit_split", {
                  split_group_id: ev.row.split_group_id,
                  id: ev.row.collection_id || ev.row.id,
                })
              );
              return;
            }
            if (ev.row.collection === "transaction") {
              dispatch(
                setActiveDialog("edit_transaction", {
                  id: ev.row.collection_id,
                })
              );
              return;
            }

            if (ev.row.collection === "inventory") {
              dispatch(
                setActiveDialog("edit_inventory", {
                  id: ev.row.collection_id,
                })
              );
              return;
            }

            if (ev.row.collection === "sale") {
              dispatch(
                setActiveDialog("edit_sale", {
                  id: ev.row.collection_id,
                })
              );
              return;
            }

            dispatch(
              setActiveDialog("edit_transaction", {
                id: ev.row.collection_id || ev.row.id,
              })
            );
          }}
        />
      </Box>
      <ConfirmDialog
        open={dialog?.type === "delete"}
        title="Confirm"
        text={
          dialog?.type === "delete"
            ? `Are you sure you want to delete ${
                dialog.length ?? dialog.items.length
              } transaction(s)?`
            : ""
        }
        onCancel={() => {
          setDialog(undefined);
        }}
        onConfirm={async () => {
          if (dialog?.type !== "delete") return;
          if (dialog.items.length) {
            await deleteMappedTransactions(dialog.items);
            toast.success("Transaction(s) deleted successfully!");
          }
          if (dialog.hasReadOnly)
            toast.error("Beginning Balance Journals can’t be deleted");
          notifySkippedSplitTransactions(dialog.skippedSplit ?? 0);
          setDialog(undefined);
        }}
      />
      <BulkEditDialog
        open={dialog?.type === "bulk-edit"}
        onClose={() => setDialog(undefined)}
      />
      <BulkEditPanel
        open={dialog?.type === "bulk-edit-panel"}
        onClose={() => setDialog(undefined)}
        selectedTransactionIds={editableTransactionIds}
        readOnlyCount={readOnlyCount}
        onOpenCSVBulkEdit={() => setDialog({ type: "bulk-edit" })}
      />
    </div>
  );
}

export default Other;
